import type { ColumnDef, TableFeatures } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import type { ComponentProps, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { isOpen, TEMPLATE_PERMISSIONS, QUESTION_PERMISSIONS } from '@hubertai/screening-question-types';
import type {
  TAnyClientQuestionType,
  TAvailableLanguageCodes,
  TOpenQuestion,
} from '@hubertai/screening-question-types';
import { Tooltip } from '@radix-ui/themes';
import {
  getRuleTargetBlockReason,
  getRuleTargetShareFailureKey,
  getRuleTargetShareGroupIds,
  getRuleTargetTooltipContent,
} from './ruleTargetReach';
import type { TRuleSource } from './ruleTargetReach';
import QuestionTableView from './QuestionTableView';
import LanguageCombobox from 'components/comboboxes/LanguageCombobox';
import QuestionTypeCombobox from 'components/comboboxes/QuestionTypeCombobox';
import ScoringCategoryCombobox from 'components/comboboxes/ScoringCategoryCombobox';
import { useVisibilityLabels } from 'utils/visibilityLabels';
import ClientCombobox from 'components/comboboxes/ClientCombobox';
import DebouncedInput from 'components/common/DebouncedInput';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import PaginatedManagerTable from 'components/PaginatedManagerTable';
import FiltersPopover from 'components/PaginatedManagerTable/FiltersPopover';
import PaginatedTableFilterCard from 'components/PaginatedManagerTable/PaginatedTableFilterCard';
import SharingLevelFilter, { CLIENT_SHARING_LEVELS } from 'components/PaginatedManagerTable/SharingLevelFilter';
import { toggleEditQuestionDialog } from 'rtk/layout/LayoutControlSlice';
import { useFindPaginatedQuestionsQuery, useShareWithAccessGroupsMutation } from 'rtk/query/apis/questions';
import type { TPaginationQuestionsFilter } from 'rtk/query/apis/questions/questions.api.types';
import { useAppDispatch } from 'rtk/query/hooks';
import { EItemType } from 'types/general.types';
import QuestionTypeBadge from 'components/Questionsv2/QuestionTypeBadge';
import { useUser } from 'hooks/useUser';
import { useMissingTemplateAccessGroups } from 'hooks/useMissingTemplateAccessGroups';
import { isHubertAdminUser, isRecruiterUser } from 'types/user.types';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';
import {
  useTemplateAccessGroupIds,
  useTemplateClientId,
  useTemplateId,
  useTemplateReferenceId,
  useTemplateVisibility,
} from 'hooks/useTemplate';
import type { TVisibility } from 'types/template/template.types';
interface Props extends ComponentProps<'div'> {
  onRowClick?: () => void;
  onRowSelect?: (x: TAnyClientQuestionType[]) => void;
  filterContainerClass?: string;
  onAddQuestion?: {
    show: boolean;
    click: (question: TAnyClientQuestionType) => void;
  };
  onCopyQuestion?: (questionId: string) => void;
  language?: TAvailableLanguageCodes;
  autoFocusSearch?: boolean;
  ruleSource?: TRuleSource;
}

const VISIBILITY_PRECEDENCE: Record<TVisibility, number> = { global: 1, client: 2, local: 3 };

export type TVisibilityMismatchReason = 'visibilityMismatch' | 'differentClient' | 'missingAccessGroups' | null;

const isNarrowerThanTemplate = (question: TAnyClientQuestionType, templateVisibility: TVisibility): boolean =>
  !!question.visibility && VISIBILITY_PRECEDENCE[templateVisibility] < VISIBILITY_PRECEDENCE[question.visibility];

const belongsToAnotherClient = (
  question: TAnyClientQuestionType,
  templateVisibility: TVisibility,
  templateClientId: string | undefined,
): boolean =>
  templateClientId !== question?.client_id && templateVisibility !== 'global' && question.visibility !== 'global';

// Single source of truth for "why can't this question be added to the template" — used both to
// decide whether the Add button is disabled and to pick the tooltip content, so the two can't drift
// apart.
export const getVisibilityMismatchReason = (
  question: TAnyClientQuestionType,
  templateVisibility: TVisibility | undefined,
  templateClientId: string | undefined,
  templateAccessGroups: string[] | undefined,
): TVisibilityMismatchReason => {
  if (!templateVisibility) return null;

  if (isNarrowerThanTemplate(question, templateVisibility)) return 'visibilityMismatch';

  if (belongsToAnotherClient(question, templateVisibility, templateClientId)) return 'differentClient';

  const missingTemplateAccessGroup =
    templateVisibility === 'local' &&
    question.visibility === 'local' &&
    templateClientId === question.client_id &&
    !(question.access_groups_ids ?? []).some((id) => (templateAccessGroups ?? []).includes(id));
  if (missingTemplateAccessGroup) return 'missingAccessGroups';

  return null;
};

/**
 * Why the Add button can't add `question` to the template, for the user at hand. A Hubert admin
 * copies a question narrower than the template, or another client's, rather than adding it as it is,
 * but may bend access groups — the backend's assertHubertAdminMayAddQuestion.
 */
export const getTemplateFitReason = (
  hasBroadEligibilityAccess: boolean,
  question: TAnyClientQuestionType,
  templateVisibility: TVisibility | undefined,
  templateClientId: string | undefined,
  templateAccessGroups: string[] | undefined,
): TVisibilityMismatchReason => {
  if (!hasBroadEligibilityAccess) {
    return getVisibilityMismatchReason(question, templateVisibility, templateClientId, templateAccessGroups);
  }
  if (!templateVisibility) return null;
  if (isNarrowerThanTemplate(question, templateVisibility)) return 'visibilityMismatch';
  if (belongsToAnotherClient(question, templateVisibility, templateClientId)) return 'differentClient';
  return null;
};

export const hasBroadQuestionTemplateEligibilityAccess = (
  user: TAdminUser | TRecruiterAppUser | undefined | null,
): boolean => isHubertAdminUser(user);

// The tooltip copy for each reason from getVisibilityMismatchReason — kept as one flat mapping
// instead of a chain of ternaries so a new reason is one more case, not one more nesting level.
export const getMismatchTooltipContent = (
  t: TFunction<'Questionsv2'>,
  reason: TVisibilityMismatchReason,
  templateVisibility: TVisibility | undefined,
  questionVisibility: TVisibility | null | undefined,
): string => {
  switch (reason) {
    case 'visibilityMismatch':
      return t('searchQuestion.tooltip.visibilityMismatch', { templateVisibility, questionVisibility });
    case 'differentClient':
      return t('searchQuestion.tooltip.differentClient');
    case 'missingAccessGroups':
      return t('searchQuestion.tooltip.missingAccessGroups');
    case null:
      return '';
  }
};

export const getCanDisableAddButton = (
  mismatchReason: TVisibilityMismatchReason,
  onAddQuestion: { show: boolean; click: (question: TAnyClientQuestionType) => void } | undefined,
): boolean => !!mismatchReason && !!onAddQuestion?.show;

const SearchQuestion = ({
  filterContainerClass,
  onRowClick,
  onRowSelect,
  onAddQuestion,
  onCopyQuestion,
  language,
  autoFocusSearch,
  ruleSource,
  ...props
}: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<TPaginationQuestionsFilter>({
    text: searchParams.get('search') ?? '',
    questionType: [],
    language: [],
    scoringCategory: [],
    starQuestion: false,
    visibility: [],
  });
  const [sortingState, setSortingState] = useState([{ id: 'created_at', desc: true }]);
  const { visibilityLabel } = useVisibilityLabels();
  const user = useUser();
  const templateId = useTemplateId();
  const templateReferenceId = useTemplateReferenceId();
  const templateVisibility = useTemplateVisibility();
  const templateClientId = useTemplateClientId();
  const templateAccessGroups = useTemplateAccessGroupIds();
  const isRecruiter = isRecruiterUser(user);
  const hasBroadEligibilityAccess = hasBroadQuestionTemplateEligibilityAccess(user);
  // Copying into the template creates a new question that the template has to be able to deliver, so
  // it is gated exactly like creating one from scratch — including for a global or client source,
  // since the copy takes the template's scope rather than the source question's.
  const { isCreateBlocked, missingAccessGroupLabel, isResolvingAccessGroups } = useMissingTemplateAccessGroups();
  const [shareWithAccessGroups, { isLoading: isSharingRuleTarget }] = useShareWithAccessGroupsMutation();
  const showCopyButton = useMemo(() => {
    const isCopyQuestion = onCopyQuestion ? true : false;

    if (isHubertAdminUser(user)) {
      return isCopyQuestion;
    }

    if (isRecruiter) {
      const hasPermissions =
        user.access_groups_permissions?.some(
          (ag) =>
            ag.permissions.includes(QUESTION_PERMISSIONS.Create) &&
            ag.resources.some(
              (r) => (r.resourceId === templateReferenceId || r.resourceId === templateId) && r.type === 'template',
            ),
        ) ||
        user.access_groups_permissions?.some((ag) => ag.permissions.includes(QUESTION_PERMISSIONS.ManageClientLevel)) ||
        user.access_groups_permissions?.some(
          (ag) =>
            ag.permissions.includes(TEMPLATE_PERMISSIONS.ShareOrg) &&
            ag.permissions.includes(QUESTION_PERMISSIONS.Create),
        );
      return (hasPermissions ?? false) && isCopyQuestion;
    }
    return false;
  }, [onCopyQuestion, user, templateReferenceId, templateId, isRecruiter]);

  const columns = useMemo<
    ColumnDef<TableFeatures, TAnyClientQuestionType & { client?: { orgName: string } | undefined }>[]
  >(
    () => [
      {
        id: 'short_description',
        enableColumnFilter: false,
        header: () => <span>{t('searchQuestion.columns.question')}</span>,
        cell: (info) => {
          const question = info.row.original;
          return <QuestionTableView question={question} language={language} />;
        },
      },
      {
        id: 'type',
        enableColumnFilter: false,
        header: () => <span>{t('searchQuestion.columns.type')}</span>,
        cell: (info) => {
          const question = info.row.original;
          return <QuestionTypeBadge question={question} />;
        },
      },
      {
        id: 'classification',
        enableColumnFilter: false,
        enableSorting: false,
        header: () => <span>{t('searchQuestion.columns.classification')}</span>,
        cell: (info) => {
          const classification = info.row.original.classification_names;
          if (!classification) return null;
          return (
            <div className="grid max-w-[260px] grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
              <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {t('classification.type')}
              </span>
              <HubertBadge variant="info" className="w-fit">
                {classification.type}
              </HubertBadge>
              {classification.topics.length > 0 && (
                <>
                  <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                    {t('classification.topics')}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {classification.topics.map((topic) => (
                      <HubertBadge
                        key={topic}
                        variant="secondary"
                        className="border-gray-200 bg-white font-normal text-gray-700 hover:bg-white"
                      >
                        {topic}
                      </HubertBadge>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        },
      },
      {
        id: 'available_languages',
        enableColumnFilter: false,
        enableSorting: false,
        header: () => <span>{t('searchQuestion.columns.languages')}</span>,
        cell: (info) => {
          const question = info.row.original;
          const available = question.available_languages;
          if (!available || !available.length) {
            return <div>{t('searchQuestion.noLanguages')}</div>;
          }
          return (
            <div className="flex max-w-[200px] flex-row justify-between text-wrap">
              {available.slice(0, 2).map((x) => {
                return (
                  <HubertBadge key={x} variant={'info'} className="ml-1">
                    {x}
                  </HubertBadge>
                );
              })}
              {available.length > 2 ? (
                <span>{t('searchQuestion.moreLanguages', { count: available.length - 2 })}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'id',
        header: () => <span>{t('searchQuestion.columns.id')}</span>,
        accessorFn: (row) => {
          return row.id;
        },
        cell: (info) => {
          const id = info.getValue() as string;
          return (
            <HubertBadge className="" variant={'outline'}>
              {id}
            </HubertBadge>
          );
        },
      },
      {
        id: 'visibility',
        header: () => <span>{t('searchQuestion.columns.sharedWith')}</span>,
        enableSorting: false,
        cell: (info) => {
          const question = info.row.original;
          const visibility = question.visibility || 'global';
          const clientId = question.client_id as string | undefined;
          const clientName = question.client?.orgName as string | undefined;
          // A global question reaches every client regardless of the client_id still on the document,
          // so naming one here would be misleading.
          const showClient = !!clientId && visibility !== 'global' && isHubertAdminUser(user);
          return (
            <div className="flex flex-col items-start gap-0.5">
              <HubertBadge
                variant={visibility === 'global' ? 'success' : visibility === 'client' ? 'info' : 'outline'}
                className="w-fit"
              >
                {visibilityLabel(visibility)}
              </HubertBadge>
              {showClient && <span className="text-muted-foreground text-xs">{clientName ?? `ID: ${clientId}`}</span>}
            </div>
          );
        },
      },
      {
        id: 'addQuestionButton',
        header: () => <span></span>,
        cell: (info) => {
          const question = info.row.original as TAnyClientQuestionType;

          const openQuestion = isOpen(question) && isRecruiterUser(user) ? (question as TOpenQuestion<string>) : null;
          const isStarOrScoringQuestion =
            !!openQuestion?.is_star_question || (openQuestion?.scoring_category?.length ?? 0) > 0;

          const disableCopyButton = !showCopyButton || isStarOrScoringQuestion || isCreateBlocked;
          const showCopyTooltip = isRecruiter && disableCopyButton;
          const copyDisabledTooltipContent = isCreateBlocked
            ? t('accessGroups.missingTemplateAccessGroups', { accessGroups: missingAccessGroupLabel })
            : t('searchQuestion.copyDisabledTooltip');

          const templateFitReason = getTemplateFitReason(
            hasBroadEligibilityAccess,
            question,
            templateVisibility,
            templateClientId,
            templateAccessGroups,
          );
          const mismatchReason = ruleSource
            ? getRuleTargetBlockReason(ruleSource, question, { isHubertAdmin: isHubertAdminUser(user) })
            : templateFitReason;

          const addButtonTooltipContent =
            ruleSource?.kind === 'question'
              ? getRuleTargetTooltipContent(t, mismatchReason)
              : getMismatchTooltipContent(t, mismatchReason, templateVisibility, question.visibility);
          const showAddButtonTooltip = mismatchReason !== null;

          const canDisableAddButton = getCanDisableAddButton(mismatchReason, onAddQuestion);

          const shareGroupIds =
            ruleSource && onAddQuestion?.show
              ? getRuleTargetShareGroupIds(ruleSource, mismatchReason, {
                  isHubertAdmin: isHubertAdminUser(user),
                  memberGroupIds: isRecruiterUser(user)
                    ? (user.access_groups_permissions ?? []).map((ag) => ag.accessGroupId)
                    : [],
                })
              : [];
          const shareTooltipContent =
            ruleSource?.kind === 'template'
              ? t('searchQuestion.ruleTargetShare.tooltipTemplate')
              : t('searchQuestion.ruleTargetShare.tooltipQuestion');
          const shareRuleTarget = async () => {
            try {
              const { results } = await shareWithAccessGroups({
                questionIds: [question.id],
                accessGroupIds: shareGroupIds,
              }).unwrap();
              const result = results.find((x) => x.questionId === question.id);
              if (result?.shared) {
                toast.success(t('searchQuestion.ruleTargetShare.shared'));
                return;
              }
              const refusal = result?.shared === false ? result.reason : undefined;
              toast.error(t(`searchQuestion.ruleTargetShare.failed.${getRuleTargetShareFailureKey(refusal)}`));
            } catch {
              toast.error(t('searchQuestion.ruleTargetShare.failed.unknown'));
            }
          };
          const shareButton = (
            <HubertButton
              size={'xs'}
              variant={'outline'}
              loading={isSharingRuleTarget}
              onClick={(e) => {
                e.stopPropagation();
                void shareRuleTarget();
              }}
            >
              {t('searchQuestion.ruleTargetShare.button')}
            </HubertButton>
          );

          const withTooltip = (show: boolean, content: string, element: ReactNode) =>
            show ? (
              <Tooltip content={content}>
                <span className="ml-1 inline-flex">{element}</span>
              </Tooltip>
            ) : (
              <span className="ml-1 inline-flex">{element}</span>
            );

          const addButton = (
            <HubertButton
              size={'xs'}
              disabled={canDisableAddButton}
              onClick={(e) => {
                if (onAddQuestion?.show && onAddQuestion.click) {
                  onAddQuestion.click(question);
                }
                e.stopPropagation();
              }}
            >
              {t('searchQuestion.addButton')}
            </HubertButton>
          );
          const copyButton = (
            <HubertButton
              size={'xs'}
              // Also disables the button, so a click can't land before the template's access groups
              // have resolved. In practice they have, long before the library panel is opened.
              loading={isResolvingAccessGroups}
              disabled={disableCopyButton}
              onClick={(e) => {
                if (onCopyQuestion) {
                  onCopyQuestion(question.id);
                }
                e.stopPropagation();
              }}
            >
              {t('searchQuestion.copyButton')}
            </HubertButton>
          );
          return (
            <>
              {withTooltip(showAddButtonTooltip, addButtonTooltipContent, addButton)}
              {shareGroupIds.length ? withTooltip(true, shareTooltipContent, shareButton) : null}
              {withTooltip(showCopyTooltip, copyDisabledTooltipContent, copyButton)}
            </>
          );
        },
      },
    ],
    [
      t,
      visibilityLabel,
      onAddQuestion,
      language,
      user,
      showCopyButton,
      isRecruiter,
      hasBroadEligibilityAccess,
      templateVisibility,
      templateClientId,
      templateAccessGroups,
      onCopyQuestion,
      isCreateBlocked,
      missingAccessGroupLabel,
      isResolvingAccessGroups,
      ruleSource,
      shareWithAccessGroups,
      isSharingRuleTarget,
    ],
  );

  const hiddenFilterCount = [
    filter.questionType?.length,
    filter.language?.length,
    filter.scoringCategory?.length,
    filter.starQuestion,
  ].filter(Boolean).length;

  const hasRowActions = !!onAddQuestion?.show || !!onCopyQuestion;
  const visibleColumns = useMemo(
    () => (hasRowActions ? columns : columns.filter((column) => column.id !== 'addQuestionButton')),
    [columns, hasRowActions],
  );

  return (
    <div {...props} className="flex h-full w-full flex-col justify-between gap-4">
      <PaginatedTableFilterCard className={filterContainerClass}>
        <div className="flex w-full flex-col justify-between gap-2">
          <DebouncedInput
            placeholder={t('searchQuestion.searchPlaceholder')}
            type="text"
            autoFocus={autoFocusSearch}
            value={filter.text}
            onChange={(v) => {
              setFilter({ ...filter, text: v as string });
              setSearchParams({ search: v as string });
            }}
          />

          <div className="flex flex-wrap items-center justify-start gap-4">
            <SharingLevelFilter
              value={{ visibility: filter.visibility ?? [], accessGroupIds: filter.accessGroupIds ?? [] }}
              onChange={(value) => setFilter({ ...filter, ...value })}
              clientId={filter.clientId}
            />

            {isHubertAdminUser(user) && (
              <ClientCombobox
                isMulti={false}
                value={filter.clientId ?? ''}
                onValueChange={(value) =>
                  setFilter({
                    ...filter,
                    clientId: value as string,
                    accessGroupIds: [],
                    visibility: value ? CLIENT_SHARING_LEVELS : filter.visibility,
                  })
                }
              />
            )}

            <div className="ml-auto">
              <FiltersPopover activeCount={hiddenFilterCount}>
                <div className="flex w-full flex-col justify-start gap-4">
                  <div className="flex w-full flex-row justify-start gap-2">
                    <QuestionTypeCombobox
                      isMulti={true}
                      onValueChange={(value) => {
                        setFilter({
                          ...filter,
                          questionType: value as string[],
                        });
                      }}
                      value={filter.questionType ?? []}
                    />

                    <LanguageCombobox
                      isMulti={true}
                      onValueChange={(value) => {
                        setFilter({
                          ...filter,
                          language: value as string[],
                        });
                      }}
                      value={filter.language}
                    />
                  </div>

                  <ScoringCategoryCombobox
                    isMulti={true}
                    disabled={filter && !filter.questionType?.includes('open')}
                    // @ts-ignore
                    value={filter.scoringCategory ?? []}
                    onValueChange={(value) => {
                      setFilter({
                        ...filter,
                        scoringCategory: value as string[],
                      });
                    }}
                  />

                  <div className="hubert-vertical-center flex w-full flex-row justify-start gap-2">
                    <HubertSwitch
                      id="search-question-star-switch"
                      disabled={filter && !filter.questionType?.includes('open')}
                      onCheckedChange={(value) => {
                        setFilter({
                          ...filter,
                          starQuestion: value,
                        });
                      }}
                      checked={filter.starQuestion!}
                    />
                    <HubertLabel
                      htmlFor="search-question-star-switch"
                      className={filter && !filter.questionType?.includes('open') ? 'text-gray-400' : ''}
                    >
                      {t('searchQuestion.starQuestion')}
                    </HubertLabel>
                  </div>

                  <div className="hubert-vertical-center flex w-full flex-row justify-start gap-2">
                    <HubertButton
                      variant={'info'}
                      size={'sm'}
                      onClick={() => {
                        setFilter({
                          questionType: [],
                          language: [],
                          scoringCategory: [],
                          starQuestion: false,
                          text: 'Default Intent Response',
                          visibility: [],
                        });
                      }}
                    >
                      {t('searchQuestion.defaultIntentResponses')}
                    </HubertButton>
                  </div>
                </div>
              </FiltersPopover>
            </div>
          </div>
        </div>
      </PaginatedTableFilterCard>

      <div className="h-full overflow-auto">
        <PaginatedManagerTable
          itemType={EItemType.Questions}
          columns={visibleColumns}
          useData={useFindPaginatedQuestionsQuery}
          filter={filter}
          rowSelection={{
            enabled: true,
            onRowSelect,
          }}
          sortingState={sortingState}
          setSortingState={setSortingState}
          onRowClick={(x) => {
            // x: the question === questionConfig
            // y: current list of questions in table

            if (onRowClick) {
              onRowClick();
            } else {
              // default on row click
              dispatch(
                toggleEditQuestionDialog({
                  show: true,
                  // @ts-ignore
                  data: { question: x },
                }),
              );
            }
          }}
        />
      </div>
    </div>
  );
};

export default SearchQuestion;
